import {useState} from 'react';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useNavigate, useParams} from 'react-router-dom';
import {FeedbackDetail} from '../components/FeedbackDetail';
import {useFeedbackDetail} from '../hooks/useFeedback';
import {useAuth} from '../hooks/useAuth';
import {deleteFeedback} from '../services/feedbackService';
import {ADMIN_ROOT} from '../routes';

export function FeedbackDetailPage() {
    const t = useTraduire();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { feedback, loading, error, saving, updateFeedback } = useFeedbackDetail(id);
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);

  /**
   * Supprime la demande, apres l'avoir fait dire par son titre.
   *
   * La confirmation nomme le sujet plutot que de demander « etes-vous sur ? » :
   * on clique « oui » sans lire, on ne clique pas « oui » devant le mauvais
   * intitule. Et le geste est irreversible — ni corbeille, ni annulation.
   */
  const handleDelete = async () => {
    if (!id || !feedback) return;
    const intitule = feedback.subject || feedback.topic ||
      (feedback.message || '').slice(0, 60) || id;
    if (!window.confirm(
      `Supprimer définitivement cette demande ?\n\n« ${intitule} »\n\n` +
      `La conversation part avec elle. C'est sans retour.`)) {
      return;
    }
    setDeleting(true);
    try {
      await deleteFeedback(id);
      navigate(ADMIN_ROOT);
    } catch (erreur) {
      setDeleting(false);
      window.alert(
        `Suppression impossible : ${erreur instanceof Error ? erreur.message : erreur}`);
    }
  };

  const handleBack = () => {
    // Use browser history to go back, preserving filters and scroll position
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(ADMIN_ROOT);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-600">{t(T.chargementDesDemandes)}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
        Feedback not found.
      </div>
    );
  }

  const handleUpdate = async (updates: Parameters<typeof updateFeedback>[0]) => {
    await updateFeedback(updates, user?.email || 'unknown');
  };

  return (
    <div>
      <div className="mb-6">
        <button
            onClick={handleBack}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t(T.retourAuTableauDeBord)}
        </button>

        {/* La suppression se tient a l'oppose du retour, et en discret : elle
            ne fait pas partie du travail courant, et rien ne doit inviter a
            la frôler du curseur. */}
        <button
          onClick={handleDelete}
          disabled={deleting || !feedback}
          className="float-right inline-flex items-center text-sm text-gray-400
            hover:text-red-600 disabled:opacity-40 disabled:hover:text-gray-400"
          title={t(T.supprimerDefinitivement)}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5
                 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          {deleting ? 'Suppression…' : 'Supprimer'}
        </button>
      </div>

      <FeedbackDetail
        feedback={feedback}
        onUpdate={handleUpdate}
        saving={saving}
        adminEmail={user?.email || undefined}
        adminName={user?.displayName || undefined}
      />
    </div>
  );
}
